import { ClientHostLink, H1, H2, Paragraph, XStack, YStack } from '@my/ui'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import './about.css'

const Features = [
  {
    label: 'Image Generation Based on Male Characters',
    desc: 'We offer a rich tagging system to help users express their thoughts and maximize their creativity in generating any image based on existing male characters on the platform.',
  },
  {
    label: 'Creation of New Characters Through Fusion',
    desc: 'Users can blend features from multiple existing male characters, particularly focusing on specific parts like faces and bodies, to create entirely new characters.',
  },
  {
    label: 'Community Engagement',
    desc: 'We encourage users to share their creations. The platform provides channels for sharing and communication, facilitating user interaction and exchange.',
  },
]

const Advantages = [
  {
    label: 'Diverse Male Character Library',
    desc: 'Catering to a variety of preferences, our library classifies characters based on appearance, physique, and facial features (e.g., hairstyle, skin), assisting users in finding their desired targets.',
  },
  {
    label: 'Realistic Image Style',
    desc: 'We offer a consistently realistic style of image generation, providing an immersive experience for users.',
  },
  {
    label: 'User-Friendly and Intuitive Character Fusion',
    desc: 'Our character fusion feature offers a more friendly and intuitive user experience, significantly lowering the creative barrier for users.',
  },
  {
    label: 'Focus on Male',
    desc: "Specializing in male character generation, we have a deep understanding of our users' needs.",
  },
]

export const AboutScreen = ({ showVideo = true }: { showVideo?: boolean }) => {
  const { i18n } = useTranslation()
  const ref = useRef<HTMLVideoElement>(null)
  const src =
    i18n.resolvedLanguage === 'zh'
      ? 'https://api.mydreamboy.com/storage/v1/object/public/static/slogan-video_zh.mp4'
      : 'https://api.mydreamboy.com/storage/v1/object/public/static/slogan_video_en.mp4'
  return (
    <YStack ai="center" gap="$4" p="$4" pb="$18">
      <H1 mt="$13" ta="center">
        About Us
      </H1>
      <Paragraph theme="alt1" w={800} ta="center" size="$5" $sm={{ w: '90%' }}>
        <ClientHostLink style={{ textDecoration: 'none' }} fos="$5" />
        is a platform dedicated to the creation of male characters, offering thousands of
        AI-generated male virtual images for users to explore.
      </Paragraph>
      {showVideo && (
        <XStack maw={920}>
          <video
            ref={ref}
            autoPlay
            controls
            loop
            controlsList="nodownload noremoteplayback noplaybackrate"
            preload="auto"
            disablePictureInPicture
            disableRemotePlayback
            width="100%"
            muted
            // eslint-disable-next-line react/no-unknown-property
            webkit-playsinline="true"
            // eslint-disable-next-line react/no-unknown-property
            x5-playsinline="true"
            playsInline
            poster="https://api.mydreamboy.com/storage/v1/object/public/static/slogan_video_poster.png"
            onCanPlay={() => {
              ref.current?.play()
            }}
          >
            <source src={src} type="video/mp4" />
          </video>
        </XStack>
      )}
      <YStack
        position="relative"
        w={980}
        overflow="hidden"
        pt="$8"
        pb="$16"
        px="$8"
        mt="$12"
        ai="center"
        bw="$0.5"
        boc="$gray2"
        br="$8"
        $sm={{ w: '98%' }}
      >
        <H2 my="$8" zIndex={2}>
          Our Features
        </H2>
        <XStack zIndex={2} jc="space-between" $sm={{ flexDirection: 'column', gap: '$8' }}>
          {Features.map((item, index) => (
            <YStack key={index} w="30%" gap="$3" $sm={{ w: '100%' }}>
              <Paragraph size="$6" color="$gray12">
                {item.label}
              </Paragraph>
              <Paragraph theme="alt1" size="$5">
                {item.desc}
              </Paragraph>
            </YStack>
          ))}
        </XStack>
        <YStack position="absolute" left={0} right={0} top={0} bottom={0} zIndex={0} opacity={0.5}>
          <video
            width="100%"
            height="100%"
            muted
            playsInline
            autoPlay
            loop
            style={{ objectFit: 'cover', objectPosition: '50% 50%' }}
          >
            <source src="https://api.mydreamboy.com/storage/v1/object/public/static/about-bg.mp4?t=2023-11-13T03%3A25%3A20.255Z" />
          </video>
        </YStack>
        <YStack position="absolute" left={0} right={0} bottom={0} height={120} zIndex={0}>
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(180deg,rgba(22,22,22,0) 0%,#010208 100%)',
            }}
          />
        </YStack>
      </YStack>

      <YStack ai="center" mt="$12" gap="$10">
        <H2>Our Advantages</H2>
        <YStack w={350} gap="$8" ai="center" $sm={{ w: '90%' }}>
          {Advantages.map((item, index) => (
            <YStack key={index} gap="$4">
              <Paragraph size="$7" className="mask-gradient-right">
                {item.label}
              </Paragraph>
              <Paragraph theme="alt1" size="$5" lh="$8">
                {item.desc}
              </Paragraph>
            </YStack>
          ))}
        </YStack>
      </YStack>
    </YStack>
  )
}
